'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import useGetPayerMixReimbursement from 'src/hooks/payerMix/useGetPayerMixReimbursement';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { getProfileUrl } from 'src/utils/profileUrl';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { ReimbursementValue } from 'src/components/Profiles/shared/ReimbursementValue';

const MAX_PAYERS = 5;

interface FlatPayer {
  code: string;
  payerName: string;
  payerType: string;
  avgReimbursement: number;
  stateAvgReimbursement: number;
}

const CompactReimbursement: React.FC = () => {
  const { entityDetails } = useProfile();
  const { closeDrawer } = useDrawer();
  const widgetConfig = useWidgetConfig();

  const { type } = useParams<{ type: string }>();
  const { providerId } = getEntityIdentifiers(entityDetails);
  const profileType = entityDetails?.type || type || 'hcp';
  const filterSetId = widgetConfig?.filterSet?.id;

  const { data, isLoading } = useGetPayerMixReimbursement({
    providerId,
    location: profileType,
    filterSetId,
  });

  const flatPayers = useMemo<FlatPayer[]>(() => {
    if (!data?.results) return [];

    const rows: FlatPayer[] = [];
    for (const codeItem of data.results) {
      for (const payer of codeItem.payers ?? []) {
        rows.push({
          code: codeItem.code_name || codeItem.code,
          payerName: payer.payer_name,
          payerType: payer.payer_type,
          avgReimbursement: payer.avg_reimbursement_predicted ?? 0,
          stateAvgReimbursement: payer.state_avg_reimbursement_predicted ?? 0,
        });
      }
    }

    return rows.sort((a, b) => b.avgReimbursement - a.avgReimbursement).slice(0, MAX_PAYERS);
  }, [data]);

  const codeSetLabel = widgetConfig?.filterSet?.label || 'Reimbursement';

  return (
    <Card
      className="p-4 flex flex-col gap-3 bg-white h-full"
      data-testid="compact-reimbursement"
    >
      <WidgetHeader fallbackTitle={`Reimbursement - ${codeSetLabel}`} />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading || widgetConfig?.isLoading ? (
          <WidgetLoading data-testid="compact-reimbursement-loading" />
        ) : flatPayers.length === 0 ? (
          <WidgetEmpty data-testid="compact-reimbursement-empty" variant="full" />
        ) : (
          <div
            className="flex flex-col"
            data-testid="compact-reimbursement-table"
          >
            {/* Header */}
            <div className="flex items-center gap-2 pb-2 border-b border-neutral-200">
              <span className="text-xs font-bold text-neutral-600 flex-1 min-w-0">
                Payer
              </span>
              <span className="text-xs font-bold text-neutral-600 w-20 text-right">
                Avg. Reimb.
              </span>
              <span className="text-xs font-bold text-neutral-600 w-20 text-right">
                State Avg.
              </span>
            </div>

            {/* Rows */}
            {flatPayers.map((row, index) => (
              <div
                key={`${row.code}-${row.payerName}-${row.payerType}-${index}`}
                className="flex items-center gap-2 py-2 border-b border-neutral-100 last:border-b-0"
                data-testid={`reimbursement-row-${index}`}
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-medium text-primary truncate">
                    {row.payerName}
                  </span>
                  <span className="text-xs text-neutral-500 truncate">
                    {row.payerType} · {row.code}
                  </span>
                </div>
                <div className="text-sm text-primary w-20 text-right tabular-nums">
                  <ReimbursementValue
                    amount={row.avgReimbursement}
                    format={formatCurrency}
                  />
                </div>
                <div className="text-sm text-primary w-20 text-right tabular-nums">
                  <ReimbursementValue
                    amount={row.stateAvgReimbursement}
                    format={formatCurrency}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {flatPayers.length > 0 && (
        <Link
          href={getProfileUrl(profileType, providerId)}
          onClick={() => closeDrawer('profile')}
          className="flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 transition-colors"
          data-testid="compact-reimbursement-view-all"
        >
          View all reimbursement data
          <Icon icon={faArrowRight} className="h-3 w-3" />
        </Link>
      )}
    </Card>
  );
};

export default CompactReimbursement;
